'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Camera,
  LayoutDashboard,
  MapPin,
  MessageCircle,
  Route,
  Smartphone,
  Sparkles,
  Truck,
  Users,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingShell, { useSignUp } from '@/components/public/marketing-shell';
import HeroIllustration from '@/components/public/hero-illustration';
import StickerCard from '@/components/public/sticker-card';
import PricingTeaser from '@/components/pricing/pricing-teaser';
import {
  Circle,
  Cross,
  DashedTrail,
  Eyebrow,
  IconBadge,
  PatternPanel,
  Squiggle,
  Square,
  Triangle,
  toneAt,
  toneFill,
} from '@/components/public/decor';

const popIn = {
  initial: { opacity: 0, y: 24, scale: 0.96 },
  whileInView: { opacity: 1, y: 0, scale: 1 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] as const },
};

const SURFACES = [
  { icon: LayoutDashboard, label: 'Platform' },
  { icon: Smartphone, label: 'Logistr One app' },
  { icon: MessageCircle, label: 'WhatsApp' },
];

const CAPABILITIES = [
  {
    icon: Route,
    title: 'Smart routing',
    desc: 'Finds the best order of stops and updates automatically when things change.',
  },
  {
    icon: MapPin,
    title: 'Live tracking',
    desc: 'See every driver on the map. Customers get their own tracking link.',
  },
  {
    icon: MessageCircle,
    title: 'AI in the chat',
    desc: 'Answers drivers, updates customers and confirms jobs for you.',
  },
  {
    icon: Camera,
    title: 'Proof on every stop',
    desc: 'Photo, signature and timestamp, checked for quality automatically.',
  },
  {
    icon: Users,
    title: 'Unlimited dispatchers',
    desc: 'Add your whole team. You only pay for driver seats.',
  },
  {
    icon: Zap,
    title: 'Instant crew grouping',
    desc: 'Share a link and drivers join the mission in one tap.',
  },
];

const STEPS = [
  {
    title: 'Create the mission',
    desc: 'Add your stops or import them. The optimizer plans the best route.',
  },
  {
    title: 'Invite your crew',
    desc: 'Share one link. Drivers join from the app or WhatsApp.',
  },
  {
    title: 'Let AI handle the rest',
    desc: 'It dispatches, answers and confirms. You only step in when needed.',
  },
];

const INDUSTRIES = [
  { icon: Truck, label: 'Courier & delivery' },
  { icon: Zap, label: 'Field services' },
  { icon: Users, label: 'Home services' },
  { icon: Route, label: 'E-commerce' },
];

function HomeContent() {
  const openSignUp = useSignUp();

  return (
    <>
      {/* ─────────────── Hero ─────────────── */}
      <section className="relative overflow-hidden border-b-2 border-pop-ink">
        {/* Massive optimism circle behind the copy */}
        <Circle
          tone="amber"
          className="-left-32 -top-24 h-[26rem] w-[26rem] opacity-40 md:-left-24"
        />
        <PatternPanel pattern="dots" className="inset-y-0 right-0 hidden w-1/3 lg:block" />

        <div className="container relative px-4 py-16 md:py-24">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Eyebrow className="justify-center lg:justify-start">
                Every delivery is a mission
              </Eyebrow>

              <h1 className="mt-5 text-center font-outfit text-[2.6rem] font-extrabold leading-[1.03] sm:text-5xl lg:text-left lg:text-[3.5rem]">
                Get your crew on
                <br />
                the same mission,
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10 text-accent">in seconds.</span>
                  <Squiggle
                    tone="pink"
                    width={190}
                    className="absolute -bottom-2 left-0 w-full"
                  />
                </span>
              </h1>

              <p className="mx-auto mt-7 max-w-md text-center text-lg leading-relaxed text-muted-foreground lg:mx-0 lg:text-left">
                Group drivers, dispatchers and customers around one job
                in our app or right inside WhatsApp, with an AI assistant
                that keeps everything moving.
              </p>

              <div className="mt-9 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start">
                <Button variant="candy" size="pill" onClick={openSignUp}>
                  Start free trial
                  <span className="ml-1 flex h-6 w-6 items-center justify-center rounded-full bg-white">
                    <ArrowRight className="h-3.5 w-3.5 text-accent" strokeWidth={3} />
                  </span>
                </Button>
                <Button variant="candy-outline" size="pill" asChild>
                  <Link href="/pricing">See pricing</Link>
                </Button>
              </div>

              {/* The three places the work happens */}
              <div className="mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 lg:justify-start">
                <span className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground">
                  Works across
                </span>
                {SURFACES.map((item, i) => (
                  <span
                    key={item.label}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-pop-ink bg-card px-3 py-1.5 text-xs font-bold"
                  >
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full ${toneFill(toneAt(i))}`}
                    >
                      <item.icon className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                    </span>
                    {item.label}
                  </span>
                ))}
              </div>
            </motion.div>

            <HeroIllustration />
          </div>
        </div>
      </section>

      {/* ─────────────── Capabilities ─────────────── */}
      <section className="relative overflow-hidden py-24">
        <Square tone="mint" className="left-6 top-16 h-10 w-10 rotate-12 opacity-70" />
        <Cross tone="pink" size={26} className="right-10 top-24" />

        <div className="container relative max-w-6xl px-4">
          <motion.div {...popIn} className="mx-auto max-w-2xl text-center">
            <Eyebrow tone="pink" className="justify-center">
              What you get
            </Eyebrow>
            <h2 className="mt-4 font-outfit text-3xl font-extrabold sm:text-4xl">
              Everything your fleet needs
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              One flat plan per fleet. No per-seat fees, no surprise add-ons.
            </p>
          </motion.div>

          <DashedTrail className="left-0 right-0 top-1/2 hidden h-10 w-full lg:block" />

          <div className="relative mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((item, i) => (
              <motion.div key={item.title} {...popIn} transition={{ ...popIn.transition, delay: i * 0.06 }}>
                <StickerCard
                  interactive
                  shadow={(['accent', 'pink', 'amber', 'mint'] as const)[i % 4]}
                  className="h-full p-6 pt-9"
                >
                  {/* Icon sits half out of the top border */}
                  <div className="-mt-14 mb-4">
                    <IconBadge icon={item.icon} tone={toneAt(i)} size="lg" />
                  </div>
                  <h3 className="font-outfit text-lg font-bold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
                </StickerCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── How it works ─────────────── */}
      <section className="relative overflow-hidden border-y-2 border-pop-ink bg-card py-24">
        <PatternPanel pattern="stripes" className="inset-x-0 top-0 h-3" />
        <Circle tone="accent" className="-right-16 bottom-8 h-48 w-48 opacity-20" />

        <div className="container relative max-w-5xl px-4">
          <motion.div {...popIn} className="mx-auto max-w-2xl text-center">
            <Eyebrow tone="mint" className="justify-center">
              How it works
            </Eyebrow>
            <h2 className="mt-4 font-outfit text-3xl font-extrabold sm:text-4xl">
              Three steps to a running route
            </h2>
          </motion.div>

          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                {...popIn}
                transition={{ ...popIn.transition, delay: i * 0.1 }}
                className="relative text-center md:text-left"
              >
                <span
                  className={`inline-flex h-14 w-14 items-center justify-center rounded-full border-2 border-pop-ink font-outfit text-xl font-extrabold text-white shadow-pop ${toneFill(toneAt(i))}`}
                >
                  {i + 1}
                </span>
                <h3 className="mt-5 font-outfit text-xl font-bold">{step.title}</h3>
                <p className="mt-2 leading-relaxed text-muted-foreground">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── Industries ─────────────── */}
      <section className="relative overflow-hidden py-24">
        <div className="container relative max-w-4xl px-4 text-center">
          <motion.div {...popIn}>
            <Eyebrow tone="amber" className="justify-center">
              Who it&apos;s for
            </Eyebrow>
            <h2 className="mt-4 font-outfit text-3xl font-extrabold sm:text-4xl">
              Built for teams that move things
            </h2>
          </motion.div>

          <div className="mt-12 grid grid-cols-2 gap-5 lg:grid-cols-4">
            {INDUSTRIES.map((item, i) => (
              <motion.div key={item.label} {...popIn} transition={{ ...popIn.transition, delay: i * 0.07 }}>
                <StickerCard interactive shadow="soft" className="flex h-full flex-col items-center gap-3 p-6">
                  <IconBadge icon={item.icon} tone={toneAt(i)} />
                  <p className="text-sm font-bold">{item.label}</p>
                </StickerCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── Pricing ─────────────── */}
      <PricingTeaser />

      {/* ─────────────── Closing CTA ─────────────── */}
      <section className="relative overflow-hidden border-t-2 border-pop-ink bg-accent py-24 text-white">
        <PatternPanel pattern="dots" className="inset-0 opacity-30" />
        <Circle tone="amber" className="-left-16 -top-12 h-52 w-52 opacity-30" />
        <Circle tone="pink" className="-bottom-16 right-8 h-44 w-44 opacity-30" />
        <Triangle tone="mint" size={40} className="right-1/4 top-10 rotate-[20deg] opacity-60" />

        <div className="container relative max-w-3xl px-4 text-center">
          <motion.div {...popIn}>
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-pop-ink bg-pop-amber px-4 py-1.5 text-xs font-extrabold uppercase tracking-wider text-pop-ink">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
              7 days free
            </span>

            <h2 className="mt-6 font-outfit text-4xl font-extrabold leading-tight sm:text-5xl">
              Put your next run
              <br />
              on autopilot
            </h2>

            <p className="mx-auto mt-5 max-w-md text-lg text-white/80">
              Full Enterprise capability for a week. No credit card, cancel anytime.
            </p>

            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button variant="candy-ink" size="pill-lg" onClick={openSignUp}>
                Start free trial
                <span className="ml-1 flex h-6 w-6 items-center justify-center rounded-full bg-white">
                  <ArrowRight className="h-3.5 w-3.5 text-pop-ink" strokeWidth={3} />
                </span>
              </Button>
              <Button
                variant="candy-outline"
                size="pill-lg"
                className="border-white text-white hover:bg-white hover:text-accent"
                asChild
              >
                <Link href="/contact">Talk to sales</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default function HomePage() {
  return (
    <MarketingShell>
      <HomeContent />
    </MarketingShell>
  );
}
